import { Radio as AntdRadio, CheckboxRef } from 'antd'
import { Overwrite } from 'utility-types'
import {
  RadioGroupProps as AntdRadioGroupProps,
  RadioChangeEvent,
  RadioChangeEventTarget,
  RadioGroupButtonStyle,
  RadioGroupContextProps,
  RadioGroupOptionType,
  RadioProps
} from 'antd/es/radio'
import cx from 'classnames'
import React from 'react'
import './styles.scss'

interface RadioGroupProps
  extends Overwrite<
    AntdRadioGroupProps,
    {
      buttonStyle?: RadioGroupButtonStyle | CommutifiButtonStyle
    }
  > {
  /**
   * Display radio group to take full width of parent
   */
  block?: boolean
}

export type {
  RadioProps,
  RadioChangeEvent,
  RadioGroupProps,
  RadioChangeEventTarget,
  RadioGroupButtonStyle,
  RadioGroupContextProps,
  RadioGroupOptionType
}
const clsPrefix = 'app-radio-group'

const CommutifiButtonStyles = ['ghost'] as const
export declare type CommutifiButtonStyle = (typeof CommutifiButtonStyles)[number]

type ExtendedButtonStyle = RadioGroupButtonStyle | CommutifiButtonStyle

const typeSafeIsAntdRadioButtonStyle = (buttonStyle?: ExtendedButtonStyle): buttonStyle is RadioGroupButtonStyle =>
  CommutifiButtonStyles.findIndex((bs) => bs === buttonStyle) === -1

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ children, block, buttonStyle, ...rest }, ref) => (
    <div
      className={cx(
        clsPrefix,
        block && `${clsPrefix}--fluid`,
        !typeSafeIsAntdRadioButtonStyle(buttonStyle) && `${clsPrefix}-custom-style--${buttonStyle}`
      )}
    >
      <AntdRadio.Group
        ref={ref}
        buttonStyle={typeSafeIsAntdRadioButtonStyle(buttonStyle) ? buttonStyle : undefined}
        {...rest}
      >
        {children}
      </AntdRadio.Group>
    </div>
  )
)
RadioGroup.displayName = 'RadioGroup'

/**
 * This class is a wrap around [antd Radio](https://ant.design/components/radio/) component extending some of
 * the properties to follow our styling guidelines.
 *
 * Please refer to antd documentation for more details.
 *
 * @example
 * \<Radio.Group defaultValue="a"\>
 *  \<Radio.Button value="a"\>I'm at\</Radio.Button\>
 *  \<Radio.Button value="b"\>I'm at Work\</Radio.Button\>
 * \</Radio.Group\>
 */
const InternalRadio = React.forwardRef<CheckboxRef, RadioProps>(({ children, ...rest }, ref) => (
  <AntdRadio ref={ref} {...rest}>
    {children}
  </AntdRadio>
))
InternalRadio.displayName = 'Radio'

type RadioType = typeof InternalRadio
interface RadioInterface extends RadioType {
  Group: typeof RadioGroup
  Button: typeof AntdRadio.Button
}
export const Radio = InternalRadio as RadioInterface

Radio.Group = RadioGroup
Radio.Button = AntdRadio.Button

export default Radio
